import React, { useEffect, useState } from 'react';

import { getGoNoGo, GoNoGoCriterion, GoNoGoDashboard, updateGoNoGoConfig } from '../api/dashboards';

const fmtPctOrHours = (c: GoNoGoCriterion): string => {
  if (c.unit === 'hours') return `${c.observed.toFixed(2)}h (≤ ${c.threshold}h)`;
  return `${(c.observed * 100).toFixed(2)}% (vs ${(c.threshold * 100).toFixed(1)}%)`;
};

const StoplightTile: React.FC<{ criterion: GoNoGoCriterion }> = ({ criterion }) => {
  const color = criterion.passing ? '#2b8a3e' : '#c92a2a';
  const icon = criterion.passing ? '🟢' : '🔴';
  return (
    <div
      style={{
        border: `2px solid ${color}`,
        borderRadius: 8,
        padding: 14,
        background: '#fff',
        flex: '1 1 280px',
        minWidth: 280,
      }}
    >
      <div
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: 8 }}
      >
        <div style={{ fontWeight: 600, fontSize: 14, lineHeight: 1.3 }}>{criterion.label}</div>
        <span style={{ fontSize: 22 }}>{icon}</span>
      </div>
      <div style={{ marginTop: 8, fontSize: 13, color }}>{fmtPctOrHours(criterion)}</div>
      {criterion.note && (
        <div style={{ marginTop: 6, fontSize: 11, color: '#888', fontStyle: 'italic' }}>
          {criterion.note}
        </div>
      )}
    </div>
  );
};

/**
 * PI-3 Epic 4 S4.5 — pilot go/no-go stoplight + editable thresholds.
 * Mobile-first: tiles wrap to a single column on small screens.
 */
const AdminGoNoGoDashboard: React.FC = () => {
  const [data, setData] = useState<GoNoGoDashboard | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Partial<GoNoGoDashboard['config']>>({});
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const res = await getGoNoGo();
      setData(res);
      setDraft(res.config);
    } catch (e: any) {
      setError(e?.message || 'No se pudo cargar el dashboard go/no-go');
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      await updateGoNoGoConfig({
        maxReconciliationMismatchRate: draft.maxReconciliationMismatchRate,
        maxSupportCaseRate: draft.maxSupportCaseRate,
        minRecordingUptime: draft.minRecordingUptime,
        maxHoldbackReleaseDelayHours: draft.maxHoldbackReleaseDelayHours,
        minDisputeWithinSlaRate: draft.minDisputeWithinSlaRate,
      });
      setEditing(false);
      await load();
    } catch (e: any) {
      setError(e?.message || 'No se pudo guardar la configuración');
    } finally {
      setBusy(false);
    }
  };

  if (error) return <p style={{ color: '#c92a2a' }}>{error}</p>;
  if (!data) return <p>Cargando go/no-go...</p>;

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 4px' }}>
      <h1 style={{ marginBottom: 4 }}>Pilot Go / No-Go</h1>
      <p style={{ color: '#666', marginTop: 0, fontSize: 12 }}>
        Generado: {new Date(data.generatedAt).toLocaleString('es-MX')}
      </p>

      <div
        style={{
          marginTop: 16,
          padding: 18,
          border: `3px solid ${data.greenlight ? '#2b8a3e' : '#c92a2a'}`,
          borderRadius: 10,
          textAlign: 'center',
          background: data.greenlight ? '#ebfbee' : '#fff5f5',
        }}
      >
        <div
          style={{ fontSize: 32, fontWeight: 800, color: data.greenlight ? '#2b8a3e' : '#c92a2a' }}
        >
          {data.greenlight ? '🟢 GREENLIGHT' : '🔴 BLOCKED'}
        </div>
        <div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>
          {data.criteria.filter((c) => c.passing).length} / {data.criteria.length} criterios cumplen
        </div>
      </div>

      <h2 style={{ marginTop: 24 }}>Criterios</h2>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
        {data.criteria.map((c) => (
          <StoplightTile key={c.key} criterion={c} />
        ))}
      </div>

      <h2 style={{ marginTop: 24 }}>Configuración</h2>
      <p style={{ color: '#666', fontSize: 13 }}>
        CEO/CTO pueden ajustar los umbrales sin desplegar código.
        {data.config.updatedBy && (
          <>
            {' '}
            <span style={{ fontFamily: 'monospace', fontSize: 11 }}>
              último cambio por {data.config.updatedBy.slice(0, 8)} en{' '}
              {new Date(data.config.updatedAt).toLocaleString('es-MX')}
            </span>
          </>
        )}
      </p>

      {editing ? (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: 10,
            padding: 14,
            border: '1px solid #d1d5da',
            borderRadius: 8,
            background: '#fff',
          }}
        >
          {(
            [
              ['maxReconciliationMismatchRate', 'Max reconciliation mismatch (0..1)'],
              ['maxSupportCaseRate', 'Max support case rate (0..1)'],
              ['minRecordingUptime', 'Min recording uptime (0..1)'],
              ['maxHoldbackReleaseDelayHours', 'Max holdback release delay (hours)'],
              ['minDisputeWithinSlaRate', 'Min dispute within SLA (0..1)'],
            ] as const
          ).map(([key, label]) => (
            <label key={key} style={{ fontSize: 12, color: '#666' }}>
              {label}
              <input
                type="number"
                step="0.001"
                value={(draft[key] as number | undefined) ?? ''}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    [key]: e.target.value === '' ? undefined : Number(e.target.value),
                  })
                }
                style={{
                  display: 'block',
                  marginTop: 4,
                  padding: '6px 10px',
                  width: '100%',
                  boxSizing: 'border-box',
                }}
              />
            </label>
          ))}
          <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 8 }}>
            <button onClick={save} disabled={busy} style={btn('#2b8a3e')}>
              {busy ? 'Guardando...' : 'Guardar'}
            </button>
            <button
              onClick={() => {
                setEditing(false);
                setDraft(data.config);
              }}
              style={btn('#868e96')}
            >
              Cancelar
            </button>
          </div>
        </div>
      ) : (
        <button onClick={() => setEditing(true)} style={btn('#1971c2')}>
          Editar configuración
        </button>
      )}
    </div>
  );
};

const btn = (bg: string): React.CSSProperties => ({
  background: bg,
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  padding: '8px 14px',
  cursor: 'pointer',
  fontSize: 13,
  fontWeight: 600,
});

export default AdminGoNoGoDashboard;
